"use client";

import { Copy, CopyCheck } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import Modal from "@/components/ui/Modal";
import { trackOpenAICallUsCopy } from "@/lib/analytics/openai-events";

interface PhoneNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  /** Analytics source label for OpenAI lead_created on copy */
  trackingSource?: string;
}

const PhoneNumberModal: React.FC<PhoneNumberModalProps> = ({
  isOpen,
  onClose,
  phoneNumber,
  trackingSource = "phone_modal",
}) => {
  const t = useTranslations("Contact.showroom");
  const tCommon = useTranslations("Common");
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handlePreventScroll = () => {
    // Save the current scroll position
    const scrollY = window.scrollY;

    // Add styles to prevent scrolling
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    // Return a cleanup function to restore scrolling
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  };

  useEffect(() => {
    // Only apply scroll lock when modal is open
    if (isOpen) {
      const cleanup = handlePreventScroll();
      return cleanup;
    }
  }, [isOpen]);

  const handleCopyClick = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!!navigator && !!navigator.clipboard) {
      navigator.clipboard.writeText(phoneNumber);
      setIsCopied(true);
      toast.success(tCommon("phoneCopied"));
      trackOpenAICallUsCopy(trackingSource);
    } else {
      toast.error(tCommon("phoneCopyFailed"));
    }

    timeoutRef.current = setTimeout(() => {
      setIsCopied(false);
      timeoutRef.current = null;
    }, 800);
  };

  //! this effect is to clear the timeout when the component unmounts (prevent memory leak)
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="small" className="!z-100000 cursor-default rtl:text-right">
      <h3 className="urbanist text-2xl font-bold text-primary-foreground">{t("callUs")}</h3>
      <div className="inter mt-4 flex items-center justify-between rounded-xl bg-secondary-light px-4 py-2">
        <span className="text-base font-semibold text-secondary-foreground" dir="ltr">
          {phoneNumber}
        </span>
        <button
          className="cursor-pointer text-secondary-foreground"
          onClick={handleCopyClick}
          title={tCommon("copyPhone")}
          aria-label={isCopied ? tCommon("phoneCopied") : tCommon("copyPhone")}
        >
          <div className="relative h-4 w-4">
            <span
              className={`absolute inset-0 transition-all duration-300 ${
                isCopied ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100"
              }`}
            >
              <Copy size={16} />
            </span>
            <span
              className={`absolute inset-0 transition-all duration-300 ${
                isCopied ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-0 opacity-0"
              }`}
            >
              <CopyCheck size={16} />
            </span>
          </div>
        </button>
      </div>
    </Modal>
  );
};

export default PhoneNumberModal;
